import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { TabSdk } from '@techextensor/tab-sdk';
import { AuthUtils } from '../utils/auth.utils';
import { TranslationToken } from '../const/translation.token';

@Injectable({
  providedIn: 'root'
})
export class TokenRefreshService {
  // private readonly broadcastChannelService = inject(BroadcastChannelService);

  private isRefreshInProgress = false;
  private requestQueue: any = [];

  // /**
  //  * Creates an instance of the TokenRefreshService.
  //  * 
  //  * Initializes the event listener for token refresh events.
  //  */
  // constructor() {
  //   this.broadcastChannelService.tokenRefreshEmitter.subscribe((data: any) => {
  //     this.handleTokenRefreshEvent(data);
  //   });
  // }

  /**
   * Handle token refresh for API requests
   * 
   * @param request - The HTTP request to handle
   * @param next - The next HTTP handler in the chain
   * @param error - The error response from the HTTP request
   * @param authenticated - Whether the user is authenticated
   * @returns An observable that emits the HTTP event or an error
   */
  handleTokenRefresh(
    request: HttpRequest<any>,
    next: HttpHandler,
    error: HttpErrorResponse,
  ): Observable<HttpEvent<any>> {
    return new Observable(observer => {
      if (!TabSdk.store.refreshToken) {
        observer.error(error);
        return;
      }

      // Add request to queue
      this.addToQueue(request, next, observer);

      // Start refresh if not already in progress
      if (!this.isRefreshInProgress) {
        this.startRefreshProcess();
      }
    });
  }

  /**
   * Queue request without making API call (for interceptor)
   */
  queueRequest(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return new Observable(observer => {
      this.addToQueue(request, next, observer);

      // Process queue if refresh completed
      if (!this.isRefreshInProgress) {
        this.retryAllQueuedRequests();
      }
    });
  }

  /**
   * Check if refresh is currently in progress
   */
  isRefreshActive(): boolean {
    return this.isRefreshInProgress;
  }

  /**
   * Reset service state - called when user signs out/in
   */
  resetServiceState(): void {
    this.isRefreshInProgress = false;
    this.failAllQueuedRequests();
  }

  /**
   * Add request to queue with observer callbacks
   */
  private addToQueue(request: HttpRequest<any>, next: HttpHandler, observer: any): void {
    this.requestQueue.push({
      request,
      next,
      resolve: (value: HttpEvent<any>) => {
        if (value.type !== 0) observer.next(value);
        if (value.type === 4) observer.complete();
      },
      reject: (error: any) => observer.error(error)
    });
  }

  /**
   * Start the refresh process
   */
  private startRefreshProcess(): void {
    this.isRefreshInProgress = true;
    // this.broadcastRefreshInProgress();

    const refreshToken = TabSdk.store.refreshToken;

    if (!refreshToken) {
      this.handleRefreshFailure();
      return;
    }

    // Make refresh API call
    TabSdk.auth.refreshToken({
      refreshToken
    })?.then((response: any) => {
      if (response?.Result?.accessToken) {
        this.handleRefreshSuccess(response);
      } else {
        this.handleRefreshFailure();
      }
    })?.catch(() => {
      this.handleRefreshFailure();
    });
  }

  /**
   * Handle successful token refresh. Persists the new access token and the
   * rotated refresh token. Refresh token expiry is not touched — the server
   * validates the refresh token on every call, and `expiresIn` in the
   * response is the access-token lifetime (not the refresh-token lifetime).
   */
  private handleRefreshSuccess(response: any): void {
    const result = response?.Result;
    TabSdk.store.token = `Bearer ${result.accessToken}`;
    if (result.refreshToken) {
      TabSdk.store.refreshToken = result.refreshToken;
    }

    // this.broadcastTokenRefreshed(result.accessToken);
    this.retryAllQueuedRequests();
    this.isRefreshInProgress = false;
  }

  /**
   * Handle refresh failure - unified error handling
   */
  private handleRefreshFailure(): void {
    // this.broadcastTokenExpired();
    this.handleRefreshFailureWithoutBroadcast();
  }

  private handleRefreshFailureWithoutBroadcast(): void {
    TabSdk.ui.showError({ message: TranslationToken.TokenRefreshFailed });
    this.failAllQueuedRequests();
    this.isRefreshInProgress = false;
  }

  /**
   * Retry all queued requests with new token
   */
  private retryAllQueuedRequests(): void {
    const currentQueue = [...this.requestQueue];
    this.requestQueue = [];

    currentQueue.forEach(({ request, next, resolve, reject }) => {
      try {
        const updatedRequest = request.clone({
          setHeaders: { Authorization: TabSdk.store.token! }
        });

        next.handle(updatedRequest).subscribe({
          next: resolve,
          error: reject,
          complete: () => { }
        });
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * Fail all queued requests
   */
  private failAllQueuedRequests(): void {
    const currentQueue = [...this.requestQueue];
    this.requestQueue = [];
    const error = new Error(TranslationToken.TokenRefreshFailed);

    currentQueue.forEach(({ reject }) => reject(error));
  }

  // /**
  //  * Token validation methods
  //  */
  // private isCurrentTokenExpired(): boolean {
  //   const token = TabSdk.store.token;
  //   if (!token) return true;

  //   try {
  //     const expirationDate = AuthUtils._getTokenExpirationDate(token);
  //     return !expirationDate || Date.now() >= expirationDate.getTime();
  //   } catch (error) {
  //     console.error('Error checking current token expiry:', error);
  //     return true;
  //   }
  // }

  // /**
  //  * Handle token refresh events from other tabs
  //  */
  // private handleTokenRefreshEvent(data: any): void {
  //   switch (data.action) {
  //     case 'tokenRefreshed':
  //       if (data.token && !this.isRefreshInProgress) {
  //         TabSdk.store.token = `Bearer ${data.token}`;
  //         if (this.requestQueue.length > 0) {
  //           this.retryAllQueuedRequests();
  //         }
  //       }
  //       break;

  //     case 'tokenExpired':
  //       this.handleRefreshFailureWithoutBroadcast();
  //       break;

  //     case 'refreshInProgress':
  //       this.isRefreshInProgress = true;
  //       break;
  //   }
  // }

  // /**
  //  * Broadcast methods for cross-tab communication
  //  */
  // private broadcastRefreshInProgress(): void {
  //   this.broadcastChannelService.broadcastTokenRefresh({
  //     action: 'refreshInProgress'
  //   });
  // }

  // private broadcastTokenRefreshed(token: string): void {
  //   this.broadcastChannelService.broadcastTokenRefresh({
  //     action: 'tokenRefreshed',
  //     token
  //   });
  // }

  // private broadcastTokenExpired(): void {
  //   this.broadcastChannelService.broadcastTokenRefresh({
  //     action: 'tokenExpired'
  //   });
  // }
}